import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Expiring batches — FR-INV-02.12 / 02.15
 * Batches expiring within N days. Already-expired surface first for FIFO
 * rotation decisions.
 */
export function ExpiringBatchesPage() {
  const [days, setDays] = useState(30);
  const [warehouseId, setWarehouseId] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const expQuery = useQuery({
    queryKey: ['inv', 'expiring', days, warehouseId],
    queryFn: () => inventoryApi.getExpiringBatches({ days, warehouseId: warehouseId || undefined }),
  });

  const kpis = useMemo(() => {
    const rows = expQuery.data ?? [];
    let atRiskValue = 0, alreadyExpired = 0, expiringSoon = 0;
    for (const b of rows) {
      atRiskValue += b.at_risk_value ?? 0;
      if (b.days_until_expiry < 0) alreadyExpired++;
      else if (b.days_until_expiry <= 7) expiringSoon++;
    }
    return { total: rows.length, atRiskValue, alreadyExpired, expiringSoon };
  }, [expQuery.data]);

  const WINDOW_OPTIONS = [
    { value: 7,  label: '7 days' },
    { value: 14, label: '14 days' },
    { value: 30, label: '30 days' },
    { value: 60, label: '60 days' },
    { value: 90, label: '90 days' },
  ];

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Expiring batches"
        subtitle="FIFO-rotation dashboard — batches approaching or past expiry, with at-risk value."
      />

      {/* KPI strip — expiry is risk, emphasize */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Batches in window" value={kpis.total} />
        <KpiTile label="Already expired" value={kpis.alreadyExpired}
          accent={kpis.alreadyExpired > 0 ? 'danger' : 'default'} />
        <KpiTile label="Expiring ≤7 days" value={kpis.expiringSoon}
          accent={kpis.expiringSoon > 0 ? 'amber' : 'default'} />
        <KpiTile label="At-risk value" value={`${fmtCurrency(kpis.atRiskValue)}`}
          accent={kpis.atRiskValue > 0 ? 'amber' : 'default'} />
      </div>

      {/* Filter bar */}
      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                days === w.value
                  ? 'bg-canvas-elevated text-ink-900 shadow-xs'
                  : 'text-ink-600 hover:text-ink-900'
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <select className="input max-w-xs" value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}>
          <option value="">All warehouses</option>
          {whQuery.data?.map((w: any) => (
            <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
          ))}
        </select>
      </div>

      {expQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : expQuery.error ? (
        <ErrorState error={expQuery.error} />
      ) : !expQuery.data?.length ? (
        <div className="card">
          <EmptyState
            message="Nothing expiring"
            hint={`No batches are within the next ${days} days. Shelf life looks healthy.`}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Product</th>
                  <th>Warehouse</th>
                  <th className="text-right">Qty</th>
                  <th>Expiry</th>
                  <th className="text-right">Status</th>
                  <th className="text-right">At-risk value</th>
                </tr>
              </thead>
              <tbody>
                {(expQuery.data ?? []).map((b: any) => {
                  const expired = b.days_until_expiry < 0;
                  const soon = !expired && b.days_until_expiry <= 7;
                  return (
                    <tr
                      key={b.id}
                      className={expired ? 'bg-red-50/50' : soon ? 'bg-amber-50/50' : undefined}
                    >
                      <td className="font-mono text-xs font-semibold text-ink-900">
                        {b.batch_number}
                      </td>
                      <td>
                        <div className="font-medium text-ink-900">{b.product_name}</div>
                        <div className="font-mono text-2xs text-ink-500 mt-0.5">{b.sku}</div>
                      </td>
                      <td className="text-sm text-ink-700 font-mono">
                        {b.warehouse_code ?? <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-right font-mono font-semibold tabular-nums">
                        {b.qty_on_hand.toFixed(0)}
                      </td>
                      <td className="text-sm text-ink-700">
                        {new Date(b.expiry_date).toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </td>
                      <td className="text-right">
                        <ExpiryChip daysUntilExpiry={b.days_until_expiry} />
                      </td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        {b.at_risk_value !== null ? `${fmtCurrency(b.at_risk_value)}` : <span className="text-ink-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpiryChip({ daysUntilExpiry }: { daysUntilExpiry: number }) {
  if (daysUntilExpiry < 0) {
    return (
      <span className="badge bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 text-2xs">
        {Math.abs(daysUntilExpiry)}d ago
      </span>
    );
  }
  if (daysUntilExpiry === 0) {
    return (
      <span className="badge bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 text-2xs">
        today
      </span>
    );
  }
  if (daysUntilExpiry <= 7) {
    return (
      <span className="badge bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 text-2xs">
        {daysUntilExpiry}d
      </span>
    );
  }
  return (
    <span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 text-2xs">
      {daysUntilExpiry}d
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
